import { ReactNode, useId } from 'react'

// Accent colours for a Dashboard tile or panel: a gradient top rule plus a
// faint glow. Status tiles (heat) pick by band; the rest take a fixed hue, and
// anything with nothing to report yet goes slate.
export type Tone = 'sky' | 'green' | 'amber' | 'red' | 'violet' | 'indigo' | 'slate'

export const TONES: Record<Tone, { bar: string; glow: string }> = {
  sky: { bar: 'from-sky-500 via-sky-400 to-cyan-300', glow: 'from-sky-500/10' },
  green: { bar: 'from-emerald-500 via-green-400 to-lime-300', glow: 'from-emerald-500/10' },
  amber: { bar: 'from-orange-500 via-amber-400 to-yellow-300', glow: 'from-amber-500/10' },
  red: { bar: 'from-red-600 via-red-500 to-rose-400', glow: 'from-red-500/10' },
  violet: { bar: 'from-violet-500 via-purple-400 to-fuchsia-300', glow: 'from-violet-500/10' },
  indigo: { bar: 'from-indigo-500 via-indigo-400 to-sky-300', glow: 'from-indigo-500/10' },
  slate: { bar: 'from-slate-600 via-slate-500 to-slate-400', glow: 'from-slate-500/5' },
}

interface TerminalPanelProps {
  title: string
  tone: Tone
  /** Sits under the title: counts, a caption. */
  subtitle?: ReactNode
  /** Right side of the header: a link or a status pill. */
  action?: ReactNode
  children: ReactNode
  className?: string
}

/**
 * A Dashboard panel in the stat tiles' style: the tone's gradient rule across
 * the top and a glow that fades out under the header rather than washing the
 * whole (taller) panel.
 */
const TerminalPanel = ({ title, tone, subtitle, action, children, className = '' }: TerminalPanelProps) => {
  const headingId = `panel-${useId().replace(/:/g, '')}`
  const accent = TONES[tone]
  return (
    <section
      aria-labelledby={headingId}
      data-tone={tone}
      className={`relative overflow-hidden rounded-lg border border-terminal-border bg-terminal-surface p-4 md:p-6 ${className}`}
    >
      <span aria-hidden="true" className={`pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accent.bar}`} />
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b ${accent.glow} to-transparent`}
      />
      <div className="relative">
        <header className="mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div className="min-w-0 space-y-1">
            <h2 id={headingId} className="text-xs font-medium uppercase tracking-wider text-gray-400">
              {title}
            </h2>
            {subtitle}
          </div>
          {action}
        </header>
        {children}
      </div>
    </section>
  )
}

export default TerminalPanel
